/**
 * Stale-chunk recovery (S8-36 / #1304).
 *
 * After a deploy, a still-open tab can request an old lazy-route chunk that no
 * longer exists. We reload once to land on the fresh bundle. If a *second*
 * failure happens inside the guard window (the reload didn't help — CDN edge,
 * bfcache, offline), we stop reloading and hand off to `onRepeatFailure` so the
 * user sees a visible "Reload" prompt instead of a silent blank page.
 */

export const STALE_CHUNK_RELOAD_KEY = 'mcm:chunk-reload-at';
export const STALE_CHUNK_WINDOW_MS = 10_000;

export const isStaleChunkError = (message: string): boolean =>
  message.includes('Failed to fetch dynamically imported module') ||
  message.includes('error loading dynamically imported module') ||
  message.includes('Importing a module script failed') ||
  /Loading chunk \S+ failed/i.test(message);

/**
 * Visible fallback when the automatic reload did not fix a stale chunk. Plain
 * DOM (no React) because the failure can happen before the app mounts, and
 * the failing chunk may *be* part of the app. Idempotent.
 */
export function showStaleBundleBanner(
  reload: () => void = () => globalThis.location?.reload(),
  // Injected so the util stays free of direct DOM globals (eslint no-restricted-globals)
  // and is testable; defaults to the real document when one exists.
  doc: Document | undefined = globalThis.document,
): void {
  if (!doc) return;
  if (doc.querySelector('[data-stale-bundle-banner]')) return;

  const banner = doc.createElement('div');
  banner.setAttribute('role', 'alert');
  banner.setAttribute('data-stale-bundle-banner', '');
  banner.style.cssText = [
    'position:fixed', 'top:0', 'left:0', 'right:0', 'z-index:2147483647',
    'display:flex', 'align-items:center', 'justify-content:center', 'gap:12px',
    'padding:12px 16px', 'background:#1e3a8a', 'color:#fff',
    'font:14px/1.4 system-ui,-apple-system,Segoe UI,Roboto,sans-serif',
    'box-shadow:0 2px 8px rgba(0,0,0,.25)',
  ].join(';');

  const text = doc.createElement('span');
  text.textContent = 'A new version of MyCaseMgr is available. Reload to continue.';

  const button = doc.createElement('button');
  button.type = 'button';
  button.textContent = 'Reload';
  button.style.cssText = 'padding:6px 14px;border:0;border-radius:6px;background:#fff;color:#1e3a8a;font-weight:700;cursor:pointer';
  button.addEventListener('click', () => reload());

  banner.append(text, button);
  (doc.body ?? doc.documentElement).appendChild(banner);
}

interface RecoveryDeps {
  reload: () => void;
  onRepeatFailure: () => void;
  storage?: Storage | null;
  now?: () => number;
  windowMs?: number;
}

export function createStaleChunkRecovery({
  reload,
  onRepeatFailure,
  storage,
  now = () => Date.now(),
  windowMs = STALE_CHUNK_WINDOW_MS,
}: RecoveryDeps): () => void {
  return () => {
    let canReloadSafely = false;
    try {
      if (storage) {
        const timestamp = now();
        const last = storage.getItem(STALE_CHUNK_RELOAD_KEY);
        const attemptedRecently = last !== null && timestamp - Number(last) < windowMs;
        if (!attemptedRecently) {
          const marker = String(timestamp);
          storage.setItem(STALE_CHUNK_RELOAD_KEY, marker);
          // The next document must see this guard before we navigate away.
          canReloadSafely = storage.getItem(STALE_CHUNK_RELOAD_KEY) === marker;
        }
      }
    } catch {
      // A memory-only latch cannot survive reload. If storage is blocked,
      // leave recovery to the visible manual action instead of risking a loop.
    }

    if (!canReloadSafely) {
      onRepeatFailure();
      return;
    }
    reload();
  };
}
